import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Button, Modal, Table, Tab, Nav } from "react-bootstrap";
import {
  notifyTopRight,
  notifyError,
} from "../../toast/SetToast";
import Toast from "../../toast/SetToast";
import { db } from "../../../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { useAuth } from "../../../../context/AuthContext";

const MaterialIssueRegister = (props) => {
  const { currentUser } = useAuth();

  // Data from Firestore
  const [accessoriesList, setAccessoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Tab
  const [activeTab, setActiveTab] = useState("accessory");

  // Client-side filters
  const [accessoryFilter, setAccessoryFilter] = useState("");
  const [cpapFilter, setCpapFilter] = useState("");

  // Checkbox + quantity selections
  // selectedAccessories: { [docId]: quantity }
  const [selectedAccessories, setSelectedAccessories] = useState({});
  // selectedCpaps: { [docId]: true }
  const [selectedCpaps, setSelectedCpaps] = useState({});

  // Cart
  const [cartItems, setCartItems] = useState([]);

  // Shared
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Data Loading ---
  useEffect(() => {
    if (props.show) {
      fetchData();
    }
  }, [props.show]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [accSnap, prodSnap] = await Promise.all([
        getDocs(collection(db, "accessories")),
        getDocs(query(collection(db, "products"), where("status", "==", "ว่าง"))),
      ]);
      setAccessoriesList(
        accSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
      setProductsList(
        prodSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
      notifyError("Failed to load data: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Filtered lists ---
  const filteredAccessories = useMemo(() => {
    if (!accessoryFilter.trim()) return accessoriesList;
    const q = accessoryFilter.toLowerCase();
    return accessoriesList.filter(
      (a) =>
        (a.itemCode || "").toLowerCase().includes(q) ||
        (a.itemName || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
    );
  }, [accessoriesList, accessoryFilter]);

  const filteredProducts = useMemo(() => {
    if (!cpapFilter.trim()) return productsList;
    const q = cpapFilter.toLowerCase();
    return productsList.filter(
      (p) =>
        (p.serialNumber || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q) ||
        (p.model || "").toLowerCase().includes(q)
    );
  }, [productsList, cpapFilter]);

  // --- Accessory checkbox toggle ---
  const toggleAccessory = (id) => {
    setSelectedAccessories((prev) => {
      const next = { ...prev };
      if (next[id] !== undefined) {
        delete next[id];
      } else {
        next[id] = 1;
      }
      return next;
    });
  };

  const handleAccessoryQtyChange = (id, value) => {
    const qty = Math.max(1, parseInt(value) || 1);
    setSelectedAccessories((prev) => ({ ...prev, [id]: qty }));
  };

  // Select All for accessories (visible/filtered)
  const toggleSelectAllAccessories = () => {
    const visibleIds = filteredAccessories.map((a) => a.id);
    const allSelected = visibleIds.every(
      (id) => selectedAccessories[id] !== undefined
    );
    if (allSelected) {
      // Deselect all visible
      setSelectedAccessories((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => delete next[id]);
        return next;
      });
    } else {
      // Select all visible with default qty 1
      setSelectedAccessories((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => {
          if (next[id] === undefined) next[id] = 1;
        });
        return next;
      });
    }
  };

  // --- CPAP checkbox toggle ---
  const toggleCpap = (id) => {
    setSelectedCpaps((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      return next;
    });
  };

  const toggleSelectAllCpaps = () => {
    const visibleIds = filteredProducts.map((p) => p.id);
    const allSelected = visibleIds.every((id) => selectedCpaps[id]);
    if (allSelected) {
      setSelectedCpaps((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => delete next[id]);
        return next;
      });
    } else {
      setSelectedCpaps((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => {
          next[id] = true;
        });
        return next;
      });
    }
  };

  // --- Add Selected to Cart ---
  const handleAddSelectedToCart = () => {
    const newItems = [];
    let duplicateCount = 0;

    // Accessories
    Object.entries(selectedAccessories).forEach(([docId, qty]) => {
      const acc = accessoriesList.find((a) => a.id === docId);
      if (!acc) return;
      newItems.push({
        materialType: "Accessory",
        itemId: docId,
        itemName: acc.itemName || acc.name || acc.itemCode || "",
        itemDetails: acc.category || "",
        quantity: qty,
        _raw: acc,
      });
    });

    // CPAPs (skip duplicates already in cart)
    Object.keys(selectedCpaps).forEach((docId) => {
      const prod = productsList.find((p) => p.id === docId);
      if (!prod) return;
      const alreadyInCart = cartItems.some(
        (ci) => ci.materialType === "CPAP" && ci.itemId === docId
      );
      if (alreadyInCart) {
        duplicateCount++;
        return;
      }
      newItems.push({
        materialType: "CPAP",
        itemId: docId,
        itemName:
          `${prod.brand || ""} ${prod.model || ""}`.trim() ||
          prod.serialNumber ||
          "",
        itemDetails: prod.serialNumber || "",
        quantity: 1,
        _raw: prod,
      });
    });

    if (newItems.length === 0 && duplicateCount === 0) {
      notifyError("Please select at least one item");
      return;
    }

    if (duplicateCount > 0) {
      notifyError(
        `${duplicateCount} CPAP(s) already in the list — skipped`
      );
    }

    setCartItems((prev) => [...prev, ...newItems]);
    setSelectedAccessories({});
    setSelectedCpaps({});
  };

  // --- Cart ---
  const handleRemoveFromCart = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Reset ---
  const handleOnHideModal = () => {
    resetForm();
    props.hide();
  };

  const resetForm = () => {
    setCartItems([]);
    setSelectedAccessories({});
    setSelectedCpaps({});
    setAccessoryFilter("");
    setCpapFilter("");
    setReason("");
    setActiveTab("accessory");
  };

  // --- Sequence & Submit (unchanged logic) ---
  const getNextSequence = async () => {
    const sequenceRef = doc(db, "sequenceTracker", "materialIssueSequence");
    const sequenceDoc = await getDoc(sequenceRef);

    if (!sequenceDoc.exists()) {
      const now = new Date();
      const year = (now.getFullYear() + 543).toString().slice(-2);
      const month = (now.getMonth() + 1).toString().padStart(2, "0");
      await setDoc(sequenceRef, {
        current: 1,
        month: month,
        year: year,
      });
      return { sequence: "001", year, month, current: 1 };
    }

    const data = sequenceDoc.data();
    const now = new Date();
    const currentYear = (now.getFullYear() + 543).toString().slice(-2);
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, "0");

    if (data.month !== currentMonth || data.year !== currentYear) {
      await setDoc(sequenceRef, {
        current: 1,
        month: currentMonth,
        year: currentYear,
      });
      return {
        sequence: "001",
        year: currentYear,
        month: currentMonth,
        current: 1,
      };
    }

    const sequence = data.current.toString().padStart(3, "0");
    return {
      sequence,
      year: data.year,
      month: data.month,
      current: data.current,
    };
  };

  const updateSequence = async (newCurrent) => {
    const sequenceRef = doc(db, "sequenceTracker", "materialIssueSequence");
    await updateDoc(sequenceRef, { current: newCurrent });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!props.canCreate) {
      notifyError("You do not have permission to create material issue requests.");
      return;
    }

    if (cartItems.length === 0) {
      notifyError("Please add at least one item to the list");
      return;
    }
    if (!reason.trim()) {
      notifyError("Please enter a reason");
      return;
    }

    setIsSubmitting(true);

    try {
      const { year, month, current } = await getNextSequence();
      let seqNum = current;
      const createdRequests = [];

      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];
        const seq = seqNum.toString().padStart(3, "0");
        const requestNo = `MIR-${year}${month}-${seq}`;

        const formData = {
          requestNo,
          materialType: item.materialType,
          itemId: item.itemId,
          itemName: item.itemName,
          itemDetails: item.itemDetails,
          quantity: item.quantity,
          reason,
          status: "Pending",
          requestedBy: currentUser.displayName || "",
          requestedByEmail: currentUser.email || "",
          requestedAt: new Date(),
          approvedBy: null,
          approvedAt: null,
          rejectedBy: null,
          rejectedAt: null,
          rejectionReason: null,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        await addDoc(collection(db, "materialIssues"), formData);
        createdRequests.push({ ...formData });
        seqNum++;
      }

      await updateSequence(seqNum);

      // Send one email to approvers listing all items
      const approversSnapshot = await getDocs(
        query(
          collection(db, "approvers"),
          where("status", "==", "Available")
        )
      );
      const approverEmails = approversSnapshot.docs.map(
        (d) => d.data().email
      );

      if (approverEmails.length > 0) {
        const firstReqNo = createdRequests[0].requestNo;
        const lastReqNo =
          createdRequests[createdRequests.length - 1].requestNo;
        const subject =
          createdRequests.length === 1
            ? `New Material Issue Request: ${firstReqNo}`
            : `New Material Issue Requests: ${firstReqNo} - ${lastReqNo}`;

        const itemRows = createdRequests
          .map(
            (r, idx) =>
              `<tr>
                <td style="border:1px solid #ddd;padding:8px;">${idx + 1}</td>
                <td style="border:1px solid #ddd;padding:8px;">${r.requestNo}</td>
                <td style="border:1px solid #ddd;padding:8px;">${r.materialType}</td>
                <td style="border:1px solid #ddd;padding:8px;">${r.itemName}</td>
                <td style="border:1px solid #ddd;padding:8px;">${r.itemDetails}</td>
                <td style="border:1px solid #ddd;padding:8px;">${r.quantity}</td>
              </tr>`
          )
          .join("");

        await setDoc(doc(collection(db, "mail")), {
          to: approverEmails,
          message: {
            subject,
            html: `
              <h3>New Material Issue Request${
                createdRequests.length > 1 ? "s" : ""
              }</h3>
              <p><strong>Requested By</strong>: ${
                currentUser.displayName || ""
              }</p>
              <p><strong>Date</strong>: ${new Date().toLocaleDateString(
                "th-TH",
                {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }
              )}</p>
              <p><strong>Reason</strong>: ${reason}</p>
              <br/>
              <table style="border-collapse:collapse;width:100%;">
                <thead>
                  <tr style="background:#f2f2f2;">
                    <th style="border:1px solid #ddd;padding:8px;">#</th>
                    <th style="border:1px solid #ddd;padding:8px;">Request No</th>
                    <th style="border:1px solid #ddd;padding:8px;">Type</th>
                    <th style="border:1px solid #ddd;padding:8px;">Item</th>
                    <th style="border:1px solid #ddd;padding:8px;">Details</th>
                    <th style="border:1px solid #ddd;padding:8px;">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  ${itemRows}
                </tbody>
              </table>
              <br/>
              <p>Please login to the system to review and approve/reject these requests.</p>
            `,
          },
        });
      }

      notifyTopRight("บันทึกข้อมูลสำเร็จ");
      resetForm();
      props.hide();
    } catch (err) {
      console.error(err);
      notifyError("บันทึกข้อมูลผิดพลาด: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Computed ---
  const selectedAccessoryCount = Object.keys(selectedAccessories).length;
  const selectedCpapCount = Object.keys(selectedCpaps).length;
  const totalSelected = selectedAccessoryCount + selectedCpapCount;

  const allVisibleAccessoriesSelected =
    filteredAccessories.length > 0 &&
    filteredAccessories.every((a) => selectedAccessories[a.id] !== undefined);

  const allVisibleCpapsSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => selectedCpaps[p.id]);

  return (
    <Fragment>
      <Toast />
      <Modal
        className="fade bd-example-modal-lg"
        show={props.show}
        onHide={handleOnHideModal}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Material Issue Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* --- Tabs --- */}
              <Tab.Container
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
              >
                <Nav as="ul" className="nav-tabs">
                  <Nav.Item as="li">
                    <Nav.Link eventKey="accessory">
                      Accessory
                      {selectedAccessoryCount > 0 && (
                        <span className="badge badge-primary ml-1">
                          {selectedAccessoryCount}
                        </span>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item as="li">
                    <Nav.Link eventKey="cpap">
                      CPAP
                      {selectedCpapCount > 0 && (
                        <span className="badge badge-primary ml-1">
                          {selectedCpapCount}
                        </span>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>

                <Tab.Content className="pt-3">
                  {/* --- Accessory Tab --- */}
                  <Tab.Pane eventKey="accessory">
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Search by Item Code, Name, or Category..."
                      value={accessoryFilter}
                      onChange={(e) => setAccessoryFilter(e.target.value)}
                    />
                    <div
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}
                    >
                      <Table bordered size="sm" className="mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: "40px" }}>
                              <input
                                type="checkbox"
                                checked={allVisibleAccessoriesSelected}
                                onChange={toggleSelectAllAccessories}
                              />
                            </th>
                            <th>Item Name</th>
                            <th>Category</th>
                            <th>Stock</th>
                            <th style={{ width: "80px" }}>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAccessories.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                No items found
                              </td>
                            </tr>
                          ) : (
                            filteredAccessories.map((acc) => {
                              const isChecked =
                                selectedAccessories[acc.id] !== undefined;
                              return (
                                <tr key={acc.id}>
                                  <td className="py-1">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleAccessory(acc.id)}
                                    />
                                  </td>
                                  <td className="py-1">
                                    {acc.itemName || acc.name || acc.itemCode || "-"}
                                  </td>
                                  <td className="py-1">{acc.category}</td>
                                  <td className="py-1">
                                    {acc.stockQuantity || 0}
                                  </td>
                                  <td className="py-1">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      min="1"
                                      value={
                                        isChecked
                                          ? selectedAccessories[acc.id]
                                          : ""
                                      }
                                      onChange={(e) =>
                                        handleAccessoryQtyChange(
                                          acc.id,
                                          e.target.value
                                        )
                                      }
                                      disabled={!isChecked}
                                      style={{ width: "70px" }}
                                    />
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab.Pane>

                  {/* --- CPAP Tab --- */}
                  <Tab.Pane eventKey="cpap">
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Search by Serial Number, Brand, or Model..."
                      value={cpapFilter}
                      onChange={(e) => setCpapFilter(e.target.value)}
                    />
                    <div
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                      }}
                    >
                      <Table bordered size="sm" className="mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: "40px" }}>
                              <input
                                type="checkbox"
                                checked={allVisibleCpapsSelected}
                                onChange={toggleSelectAllCpaps}
                              />
                            </th>
                            <th>Serial Number</th>
                            <th>Brand</th>
                            <th>Model</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredProducts.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                No items found
                              </td>
                            </tr>
                          ) : (
                            filteredProducts.map((prod) => {
                              const isChecked = !!selectedCpaps[prod.id];
                              return (
                                <tr key={prod.id}>
                                  <td className="py-1">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleCpap(prod.id)}
                                    />
                                  </td>
                                  <td className="py-1">
                                    {prod.serialNumber}
                                  </td>
                                  <td className="py-1">{prod.brand}</td>
                                  <td className="py-1">{prod.model}</td>
                                  <td className="py-1">{prod.status}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>

              {/* --- Add Selected Button --- */}
              <div className="text-center mt-3">
                <Button
                  variant="success"
                  onClick={handleAddSelectedToCart}
                  disabled={totalSelected === 0}
                >
                  <i className="fa fa-plus mr-1"></i>
                  Add Selected to List
                  {totalSelected > 0 && ` (${totalSelected})`}
                </Button>
              </div>

              {/* --- Cart Table --- */}
              {cartItems.length > 0 && (
                <>
                  <hr />
                  <h6 className="mb-2">Items to Request</h6>
                  <Table bordered size="sm" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "40px" }}>#</th>
                        <th>Type</th>
                        <th>Item</th>
                        <th>Details</th>
                        <th style={{ width: "60px" }}>Qty</th>
                        <th style={{ width: "40px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((ci, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{ci.materialType}</td>
                          <td>{ci.itemName}</td>
                          <td>{ci.itemDetails}</td>
                          <td>{ci.quantity}</td>
                          <td>
                            <Button
                              variant="danger"
                              size="sm"
                              className="py-0 px-1"
                              onClick={() => handleRemoveFromCart(idx)}
                            >
                              <i className="fa fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* --- Reason --- */}
              <div className="form-group row m-1 mt-3">
                <label className="col-lg-3 col-form-label">
                  Reason / Note<span className="text-danger">*</span>
                </label>
                <div className="col-lg-9">
                  <textarea
                    className="form-control"
                    rows="3"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
        <div className="d-flex justify-content-center mb-3">
          <Button
            className="sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>
          <Button
            className="sm ml-2"
            variant="light"
            onClick={handleOnHideModal}
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
};

export default MaterialIssueRegister;
